import { TextField, TextAreaField, RadioField, SwitchField } from "@/components/form";
import { onboarding } from "@/features/onboarding/api/onboarding.api";
import { applyScan, scanLabels, type ScannedField, useDocScan, type ScanOutcome, StepHeading, FileDrop, ScanStatus, trimmed, ACCOUNT_TYPES } from "@/features/onboarding/components/wizard";
import { optionLabel, useT } from "@/features/onboarding/i18n";
import { docSizeMb, isDocTooLarge, MAX_DOC_FILE_MB } from "@/utils/docFile";
import { PhotoField } from "./PhotoField";
import { type StepProps, type FormState } from "./schema";

export function BankStep({ form, checkFile, onCheckFile, checkError, checkPreviousNote, onScanned, onBusyChange }: StepProps & { checkFile: File | null; onCheckFile: (f: File | null) => void; checkError?: string | null; checkPreviousNote?: string | null; onScanned?: () => void; onBusyChange?: (busy: boolean) => void }) {
  const t = useT();
  const c = form.control;
  const separateWithdraw = form.watch("separateWithdrawAccount");

  const ck = useDocScan({
    reading: t("bank.reading"),
    failed: t("bank.failed"),
    sizeError: (f) => (isDocTooLarge(f) ? t("file.tooLarge", { size: docSizeMb(f), max: MAX_DOC_FILE_MB }) : null),
    onFile: onCheckFile,
    onBusyChange,
    run: async (file, onlyEmpty): Promise<ScanOutcome> => {
      const d = await onboarding.scan(file, "voidedCheck");
      const scanned: ScannedField<FormState>[] = [
        ["bankRoutingNumber", trimmed(d.routingNumber), t("scan.routing")],
        ["bankAccountNumber", trimmed(d.accountNumber), t("scan.account")],
        ["bankName", trimmed(d.bankName), t("scan.bankName")],
      ];
      const { read, applied } = applyScan(form, scanned, { shouldValidate: form.formState.isSubmitted, onlyEmpty });
      if (read.length === 0) {
        return { kind: "error", msg: t("bank.nothingRead") };
      }
      onScanned?.();
      return {
        kind: "success",
        msg: applied.length === 0 ? t("scan.kept") : t("bank.prefilled", { fields: scanLabels(applied).join(t("common.listSep")) }),
      };
    },
  });

  return (
    <div className="space-y-5">
      <StepHeading
        title={t("bank.title")}
        subtitle={t("bank.subtitle")}
      />

      <PhotoField
        label={t("bank.photoLabel")}
        required
        hint={t("bank.photoHint")}
      >
        <FileDrop prompt={t("file.prompt")} busy={ck.busy} file={checkFile} error={checkError} previousNote={checkPreviousNote} onFile={(f) => ck.pick(f, !!checkPreviousNote)} onRemove={ck.clear} />
        <ScanStatus msg={ck.msg} kind={ck.kind} onRetry={ck.retry} />
      </PhotoField>

      <TextField control={c} name="bankName" label={t("bank.bankName")} placeholder={t("bank.bankNamePlaceholder")} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextField control={c} name="bankRoutingNumber" label={t("bank.routing")} required inputMode="numeric" maxLength={9} placeholder="123456789" format={(v) => v.replace(/\D/g, "").slice(0, 9)} hint={t("bank.routingHint")} />
        <TextField control={c} name="bankAccountNumber" label={t("bank.account")} required inputMode="numeric" maxLength={17} placeholder={t("bank.accountPlaceholder")} format={(v) => v.replace(/\D/g, "").slice(0, 17)} hint={t("bank.accountHint")} />
      </div>

      <RadioField control={c} name="depositAccountType" label={t("bank.accountType")} options={ACCOUNT_TYPES.map((a) => ({ value: a, label: optionLabel(t, "acct", a) }))} />

      <SwitchField control={c} name="separateWithdrawAccount" label={t("bank.separate")} />

      {separateWithdraw && (
        <div className="space-y-4 pl-4 border-l-2 border-border">
          <TextField control={c} name="withdrawBankName" label={t("bank.withdrawBank")} placeholder={t("bank.bankNamePlaceholder")} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField control={c} name="withdrawRoutingNumber" label={t("bank.withdrawRouting")} required inputMode="numeric" maxLength={9} placeholder="123456789" format={(v) => v.replace(/\D/g, "").slice(0, 9)} hint={t("bank.routingHint")} />
            <TextField control={c} name="withdrawAccountNumber" label={t("bank.withdrawAccount")} required inputMode="numeric" maxLength={17} format={(v) => v.replace(/\D/g, "").slice(0, 17)} hint={t("bank.accountHint")} />
          </div>
          <RadioField control={c} name="withdrawAccountType" label={t("bank.withdrawType")} options={ACCOUNT_TYPES.map((a) => ({ value: a, label: optionLabel(t, "acct", a) }))} />
        </div>
      )}

      <TextAreaField control={c} name="specialNotes" label={t("bank.notes")} placeholder={t("bank.notesPlaceholder")} hint={t("bank.notesHint")} />

      <p className="text-xs text-slate-400">
        {t("bank.privacy")}
      </p>
    </div>
  );
}
